import React, { useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSecondaryCategories } from '../../hooks/useSecondaryCategories';
import styles from './SecondaryCategories.module.css';

const SecondaryCategories = ({
    size = "Desktop 1440",
    onCategorySelect,
    selectedCategory
}) => {
    const [showLeftGradient, setShowLeftGradient] = React.useState(false);
    const [showRightGradient, setShowRightGradient] = React.useState(true);
    const containerRef = useRef(null);
    const categoryRefs = useRef({});
    const navigate = useNavigate();
    const location = useLocation();
    
    const { 
        categories, 
        isLoading,
        error,
        getCategorySlug
    } = useSecondaryCategories();

    const handleScroll = useCallback(() => {
        if (containerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
            setShowLeftGradient(scrollLeft > 0);
            setShowRightGradient(
                Math.ceil(scrollLeft + clientWidth) < scrollWidth
            );
        }
    }, []);

    const scrollToCategory = useCallback((id) => {
        const categoryElement = categoryRefs.current[id];
        if (categoryElement && containerRef.current) {
            const container = containerRef.current;
            const categoryRect = categoryElement.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            if (
                categoryRect.left < containerRect.left ||
                categoryRect.right > containerRect.right
            ) {
                let scrollLeft = container.scrollLeft;

                if (categoryRect.left < containerRect.left) {
                    scrollLeft += categoryRect.left - containerRect.left - 8;
                } else if (categoryRect.right > containerRect.right) {
                    scrollLeft += categoryRect.right - containerRect.right + 8;
                }

                container.scrollTo({
                    left: scrollLeft,
                    behavior: "smooth",
                });
            }
        }
    }, []);

    React.useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener("scroll", handleScroll);
            handleScroll();
        }

        return () => {
            if (container) {
                container.removeEventListener("scroll", handleScroll);
            }
        }
    }, [handleScroll, categories]);

    React.useEffect(() => {
        if (selectedCategory) {
            scrollToCategory(selectedCategory);
        }
    }, [selectedCategory, scrollToCategory]);

    const getHorizontalWidth = useCallback(() => {
        switch (size) {
            case "Phone":
                return 362.4;
            case "Tablet":
                return 626;
            case "Desktop 1200":
                return 1057;
            case "Desktop 1440":
            default:
                return 1210;
        }
    }, [size]);

    const handleCategoryClick = useCallback((category) => {
        // Primero, llamar al handler existente
        if (onCategorySelect) {
            onCategorySelect(category.id);
        }

        // Luego manejar la navegación
        const pathSegments = location.pathname.split('/');
        const primaryCategory = pathSegments[1]; // discover, trending, etc.
        
        // Check si estamos en una página de test
        if (primaryCategory === 'test') {
            return;
        }

        const categorySlug = category.id === 9 ? 'all' : getCategorySlug(category.name);
        navigate(`/${primaryCategory}/${categorySlug}`);
    }, [onCategorySelect, navigate, location, getCategorySlug]);

    if (isLoading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    if (error) {
        console.error('Error loading categories:', error);
        return null;
    }

    return (
        <div
            className={`${styles.wrapper} ${
                showLeftGradient ? styles.showLeftGradient : ""
            } ${!showRightGradient ? styles.hideRightGradient : ""}`}
            style={{ width: getHorizontalWidth() }}
        >
            <div className={styles.gradientLeft}></div>
            <div className={styles.gradientRight}></div>
            <div
                ref={containerRef}
                className={styles.selector}
            >
                {categories.map((category) => (
                    <div
                        key={category.id}
                        ref={(el) => (categoryRefs.current[category.id] = el)}
                        className={`${styles.categoryBox} ${
                            selectedCategory === category.id ? styles.selected : ""
                        }`}
                        onClick={() => handleCategoryClick(category)}
                    >
                        <span className={`${styles.categoryText} ${
                            selectedCategory === category.id ? styles.selected : ""
                        }`}>
                            {category.name}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SecondaryCategories;